<script setup lang="ts">
    import IconList from "@/components/calendars/overlayPanels/IconList/IconList.vue";
    import IconListItem from "@/components/calendars/overlayPanels/IconList/IconListItem.vue";
    import KTag from "@/components/KTag.vue";
    import { useStore } from "@/store";
    import { ReceptforskrivningDisplay } from "@/tabs/Receptforskrivning/types";
    import { computed, toRefs } from "vue";

    const store = useStore();

    const props = defineProps<{
        data: ReceptforskrivningDisplay;
    }>();

    const { data } = toRefs(props);

    const behandlingsorsakDisplay = computed(() =>
        store.getters["nll/behandlingsorsakerById"]({ id: data?.value?.behandlingsorsak, displayMode: true })
    );
</script>

<template>
    <div class="recept-tab-forskrivning-mall">
        <div class="left">
            <IconList>
                <IconListItem v-if="data.substans" label="Substans">
                    {{ data.substans }}
                </IconListItem>
                <IconListItem v-if="data.atc" label="ATC">
                    {{ data.atc }}
                </IconListItem>
                <IconListItem v-if="data.forpackningsinnehall" label="Förpackningsinnehåll">
                    {{ data.forpackningsinnehall }}
                </IconListItem>
                <IconListItem v-if="data.antal_uttag" label="Antal uttag">{{ data.antal_uttag }}</IconListItem>
                <IconListItem v-if="data.forpackningar_per_uttag" label="Antal förpackningar per uttag">{{
                    data.forpackningar_per_uttag
                }}</IconListItem>
                <IconListItem v-if="data.tid_mellan_uttag" label="Tid mellan uttag">
                    {{ data.tid_mellan_uttag }}
                </IconListItem>
            </IconList>
            <IconList>
                <IconListItem v-if="typeof data.startforpackning === 'boolean'" label="Startförpackning">
                    {{ data.startforpackning ? "Ja" : "Nej" }}
                </IconListItem>
                <IconListItem v-if="data.substitution_tillaten" label="Substitution tillåten">
                    {{ data.substitution_tillaten ? "Ja" : "Nej" }}
                </IconListItem>
                <IconListItem v-if="data.vid_behov" label="Vid behov"> Ja </IconListItem>
                <IconListItem v-if="data.obs_overstiger_maxdosering" label="Obs. Överstiger rekommenderad dos">
                    Ja
                </IconListItem>
            </IconList>
        </div>
        <div class="separator">
            <div class="line"></div>
        </div>
        <div class="right">
            <IconList>
                <IconListItem v-if="behandlingsorsakDisplay" label="Behandlingsorsak">
                    {{ behandlingsorsakDisplay?.label }}
                </IconListItem>
                <IconListItem v-if="data.annan_orsak" label="Annan behandlingsorsak">
                    {{ data.annan_orsak }}
                </IconListItem>
                <IconListItem label="Doseringsanvisning">{{ data.doseringsanvisning }}</IconListItem>
            </IconList>
            <IconList>
                <IconListItem v-if="data.linkFass" label="Länk FASS">
                    <KTag variant="link" size="none">
                        <a :href="data.linkFass" target="_blank">Läs mer på FASS</a>
                    </KTag>
                </IconListItem>
            </IconList>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";

    .recept-tab-forskrivning-mall {
        display: flex;
        flex: 1;
        gap: 1.5rem;
        width: 100%;
        max-width: $content-max-width;

        .left,
        .right {
            display: grid;
            grid-auto-flow: row;
            grid-auto-rows: max-content;
            grid-template-columns: repeat(2, auto);
            grid-row-gap: 1.5rem;
            max-width: max-content;

            ::v-deep .icon-list {
                display: grid;
                grid-column: 1 / -1;
                grid-template-columns: subgrid;
            }

            ::v-deep .icon-list-item {
                grid-auto-flow: row;

                .content {
                    width: 100%;
                }
            }
        }

        .left {
            width: fit-content;
        }

        .separator {
            .line {
                border: 1px solid $divider-color;
                height: 100%;
            }
        }

        &.variant-simple {
            font-size: 0.9rem;
        }
    }
</style>
